import React, { useState, useEffect, useRef } from 'react';
import { Send, CheckCircle, XCircle, MessageSquare, X } from 'lucide-react';
import { useUI } from '../context/UIContext';

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

const ReviewSidebar = ({ assetType, assetId, isOpen, onClose, currentUser, dashboardOwnerId, onStatusChange, apiBase }) => {
    const [history, setHistory] = useState([]);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);
    const { showToast } = useUI();

    // Use passed API_BASE or the environment default
    const BASE_URL = apiBase || API_BASE;

    // Check if the current user is the dashboard owner (builder)
    const isOwner = currentUser?.id === dashboardOwnerId;

    useEffect(() => {
        if (isOpen && assetId) {
            fetchHistory();
        }
    }, [isOpen, assetId]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [history]);

    const fetchHistory = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${BASE_URL}/workflow/${assetType}/${assetId}/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setHistory(data);
            }
        } catch (err) {
            console.error("Failed to load history", err);
        }
    };

    const handleAction = async (actionType, text = '') => {
        if (!text && (actionType === 'comment' || actionType === 'changes_requested')) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');

            const res = await fetch(`${BASE_URL}/workflow/comment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    assetType: assetType || 'dashboard',
                    assetId: assetId,
                    actionType: actionType === 'approved' ? 'approve_stakeholder' : (actionType === 'changes_requested' ? 'reject' : 'comment'),
                    commentText: text
                })
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || 'Failed to post comment');
            }

            const resData = await res.json();

            // Notify parent of status change if the backend updated it
            if (onStatusChange) {
                onStatusChange(resData.status || actionType);
            }

            // Show Toast
            let msg = 'Action successful';
            if (actionType === 'approved') msg = 'Dashboard Approved Successfully';
            if (actionType === 'changes_requested') msg = 'Changes Requested';
            if (actionType === 'comment') msg = 'Comment Posted';

            showToast(msg, 'success');

            setComment('');
            fetchHistory();
        } catch (err) {
            console.error("Action failed", err);
            showToast(err.message || "Failed to perform action", "error");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`review-sidebar ${isOpen ? 'open' : ''}`}>
            {/* Header */}
            <div className="review-header">
                <h3>
                    <MessageSquare size={18} />
                    Review & Feedback
                </h3>
                <button onClick={onClose} className="close-btn"><X size={20} /></button>
            </div>

            {/* Chat History */}
            <div className="review-history" ref={scrollRef}>
                {history.length === 0 && (
                    <div className="empty-history">
                        <MessageSquare size={32} />
                        <p>No comments yet. Start the discussion!</p>
                    </div>
                )}
                {history.map((item) => (
                    <div key={item.id} className={`review-item ${item.user_id === currentUser?.id ? 'own' : 'other'} ${item.action_type !== 'comment' ? 'system-event' : ''}`}>
                        <div className="review-bubble">
                            {item.action_type !== 'comment' && (
                                <span className="event-label">
                                    {item.action_type.replace('_', ' ')}
                                </span>
                            )}
                            <p>{item.comment_text}</p>
                        </div>
                        <span className="review-meta">
                            {item.user_name} • {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                ))}
            </div>

            {/* Action Area */}
            <div className="review-actions">
                {/* Approve / Reject buttons - visible to everyone, but disabled for the dashboard owner */}
                <div className="quick-actions">
                    <button
                        onClick={() => handleAction('approved', 'Approved via Review Panel')}
                        className="btn-approve"
                        disabled={loading || isOwner}
                        title={isOwner ? 'You cannot approve your own dashboard' : 'Approve this dashboard'}
                        style={isOwner ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                    >
                        <CheckCircle size={16} /> Approve
                    </button>
                    <button
                        onClick={() => handleAction('changes_requested', comment)}
                        className="btn-reject"
                        disabled={loading || !comment.trim() || isOwner}
                        title={isOwner ? 'You cannot reject your own dashboard' : 'Request changes'}
                        style={isOwner ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                    >
                        <XCircle size={16} /> Request Changes
                    </button>
                </div>
                {isOwner && (
                    <p style={{ fontSize: '0.75rem', color: '#f59e0b', margin: '4px 0 0 0', textAlign: 'center' }}>
                        You cannot review your own dashboard
                    </p>
                )}

                {/* Comment Input */}
                <div className="comment-box">
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Type your feedback..."
                        rows="2"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAction('comment', comment);
                            }
                        }}
                    />
                    <button
                        onClick={() => handleAction('comment', comment)}
                        disabled={!comment.trim() || loading}
                        className="send-btn"
                    >
                        <Send size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReviewSidebar;
